import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Trophy, Medal, Crown, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLeaderboard(); }, []);

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard/?min_matches=1&limit=50`);
      setPlayers(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getRankIcon = (i) => {
    if (i === 0) return <Crown className="h-5 w-5 text-yellow-500" />;
    if (i === 1) return <Medal className="h-5 w-5 text-gray-400" />;
    if (i === 2) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="text-sm font-bold text-gray-400 w-5 text-center">{i + 1}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="leaderboard-page">
      <nav className="w-full bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_blog-56/artifacts/ar7lcvoq_WhatsApp%20Image%202026-02-21%20at%2022.08.34.jpeg" alt="Logo" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold text-blue-600">Badminton Stats</span>
          </Link>
          <Button onClick={() => navigate('/signup')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="leaderboard-signup-btn">
            Join the Leaderboard <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-3">
        <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
          <ol className="flex items-center gap-1">
            <li><Link to="/" className="hover:text-blue-600">Home</Link></li>
            <li>/</li>
            <li className="text-gray-900 font-medium">Leaderboard</li>
          </ol>
        </nav>
      </div>

      <header className="max-w-4xl mx-auto px-6 pt-6 pb-10 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3 flex items-center justify-center gap-3">
          <Trophy className="h-10 w-10 text-yellow-500" /> Global Leaderboard
        </h1>
        <p className="text-base text-gray-600">See who's dominating the court. Ranked by win rate.</p>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-20">
        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : players.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <Trophy className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No players yet. Be the first!</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 border-b text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <div className="col-span-1">#</div>
              <div className="col-span-5">Player</div>
              <div className="col-span-2 text-center">Matches</div>
              <div className="col-span-2 text-center">W / L</div>
              <div className="col-span-2 text-right">Win Rate</div>
            </div>
            {players.map((p, i) => (
              <div key={p.id} className={`grid grid-cols-12 gap-2 px-4 py-3 items-center border-b last:border-0 ${i < 3 ? 'bg-yellow-50/30' : 'hover:bg-gray-50'} transition-colors`} data-testid={`leaderboard-rank-${i}`}>
                <div className="col-span-1 flex items-center">{getRankIcon(i)}</div>
                <div className="col-span-5 flex items-center gap-2 min-w-0">
                  <Avatar className="h-8 w-8 shrink-0">
                    <AvatarImage src={p.profile_image} />
                    <AvatarFallback style={{ backgroundColor: p.banner_color }} className="text-white text-xs font-bold">
                      {p.username[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="font-medium text-sm truncate">{p.username}</span>
                </div>
                <div className="col-span-2 text-center text-sm text-gray-600">{p.total_matches}</div>
                <div className="col-span-2 text-center text-sm">
                  <span className="text-green-600 font-medium">{p.wins}</span>
                  <span className="text-gray-400 mx-0.5">/</span>
                  <span className="text-red-500 font-medium">{p.losses}</span>
                </div>
                <div className="col-span-2 text-right">
                  <span className={`text-sm font-bold ${p.win_rate >= 60 ? 'text-green-600' : p.win_rate >= 40 ? 'text-blue-600' : 'text-gray-600'}`}>
                    {p.win_rate}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 text-center bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-10 text-white">
          <h2 className="text-2xl font-bold mb-2">Think you can climb the ranks?</h2>
          <p className="text-blue-100 mb-5">Start tracking your matches and see where you stand.</p>
          <Button onClick={() => navigate('/signup')} className="bg-white text-blue-600 hover:bg-blue-50 px-6 py-2.5 font-semibold">
            Create Free Account
          </Button>
        </div>
      </main>

      <footer className="w-full bg-white border-t border-gray-200 py-6 text-center text-sm text-gray-500">
        <Link to="/" className="hover:text-blue-600 mr-4">Home</Link>
        <Link to="/features" className="hover:text-blue-600 mr-4">Features</Link>
        <Link to="/privacy" className="hover:text-blue-600 mr-4">Privacy</Link>
        <Link to="/terms" className="hover:text-blue-600">Terms</Link>
      </footer>
    </div>
  );
};

export default LeaderboardPage;
